import React, { useState, useEffect, useCallback } from 'react';
import ShooterSelector from './ShooterSelector';
import './EditShooters.css';
import { Trash2 } from 'lucide-react';
import { ApiError } from '../../../../../../lib/apiClient';
import { eventsService } from '../../../../../../services';

const EditShooters = ({ isOpen, closeModal, event, onShootersUpdated }) => {

    const [selectedShooters, setSelectedShooters] = useState([]);
    const [error, setError] = useState(null);

    const fetchEventShooters = useCallback(async () => {
        if (!event) return;
        try {
            const data = await eventsService.getEventShooters(event.eventId);
            setSelectedShooters(data || []);
        } catch (error) {
            console.error('Error fetching event shooters:', error);
            setError('Failed to load event shooters. Please try again.');
        }
    }, [event]);

    useEffect(() => {
        if (isOpen && event) {
            setSelectedShooters([]);
            setError(null);
            fetchEventShooters();
        }
    }, [isOpen, event, fetchEventShooters]);

    const addShooter = async (shooter) => {
        if (selectedShooters.some(s =>
            (shooter.appUserId && s.appUserId === shooter.appUserId) ||
            (shooter.shooterName && s.shooterName === shooter.shooterName))) {
            setError('This shooter is already added to the event.');
            return;
        }

        try {
            await eventsService.addEventShooter(event.eventId, {
                appUserId: shooter.appUserId,
                shooterName: shooter.shooterName
            });
            await fetchEventShooters();
            setError(null);
            // Notify parent component about shooter updates
            if (onShootersUpdated) onShootersUpdated();
        } catch (error) {
            console.error('Error adding shooter:', error);
            setError('Failed to add shooter. Please try again.');
        }
    };

    const removeShooter = async (shooter) => {
        try {
            await eventsService.removeEventShooter(event.eventId, shooter.eventShooterId);
            await fetchEventShooters();
            setError(null);
            // Notify parent component about shooter updates
            if (onShootersUpdated) onShootersUpdated();
        } catch (error) {
            console.error('Error removing shooter:', error);
            if (error instanceof ApiError && error.status === 400) {
                setError(error.message || 'This shooter has shot today, so you can\'t remove them from this event');
                return;
            }

            setError(error.message || 'Failed to remove shooter. Please try again.');
        }
    };

    // Custom close function to also notify parent about changes
    const handleClose = () => {
        if (onShootersUpdated) onShootersUpdated();
        closeModal();
    };

    const getDisplayName = (shooter) => shooter.shooterName || `${shooter.firstName || ''} ${shooter.lastName || ''}`.trim() || shooter.userName || 'Shooter';
    const getInitials = (name) => {
        const parts = (name || '').split(' ').filter(Boolean).slice(0, 2);
        return parts.map((part) => part[0]?.toUpperCase()).join('') || 'SC';
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="create-event-box edit-shooters-box" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Edit shooters for {event.name}</h2>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="shooter-selector-container">
                    <ShooterSelector currentShooters={selectedShooters} onShooterSelect={addShooter} />
                </div>

                <div className="current-shooters-section">
                    <h5 className="current-shooters-title">Current shooters ({selectedShooters.length})</h5>
                    <p className="current-shooters-help">
                        Add the people actually shooting today. Guests can sit alongside registered members and be scored the same way.
                    </p>
                    <div className="current-shooters-grid">
                        {selectedShooters.length > 0 ? (
                            selectedShooters.map((shooter) => (
                                <div key={shooter.eventShooterId} className="current-shooter-item">
                                    <div className="current-shooter-main">
                                        {shooter.imageUrl ? (
                                            <img
                                                src={shooter.imageUrl}
                                                alt={getDisplayName(shooter)}
                                                className="current-shooter-avatar current-shooter-avatar-image"
                                            />
                                        ) : (
                                            <span className="current-shooter-avatar">{getInitials(getDisplayName(shooter))}</span>
                                        )}
                                        <div className="current-shooter-copy">
                                            <h6>{getDisplayName(shooter)}</h6>
                                        </div>
                                    </div>
                                    <button
                                        className={`square-action red-action`}
                                        onClick={() => removeShooter(shooter)}
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <p>No shooters selected for this event.</p>
                        )}
                    </div>
                </div>

                <div className="modal-ctas mt-5">
                    <button type="button" className="dashboard-default-btn" onClick={handleClose}>
                        Close
                    </button>
                </div>

            </div>
        </div>
    );
};

export default EditShooters;
